/**
 * Duration tiers of a read pricelist (PRD 01 §3), with the operator's
 * reference LOR per bracket.
 *
 * The brackets come from the PMS (`dim_pricelist_tier`, synced daily); the
 * operator only picks the **reference LOR** inside each bracket — the
 * length of rental the engine rate-shops with for that bracket. Default is
 * the top of the range.
 *
 * Drift: brackets that survive keep their pick, new ones arrive prefilled
 * and flagged, ones the PMS dropped are struck through and purged when the
 * owner is saved.
 *
 * Extracted from `Setup/RateCards/index.jsx` (CU-869fhuhy6, UX v3 D16-a):
 * Setup › Products folds it under each row's read pricelist with a
 * face-shaped `{ read_pricelist, tier_config }`, and `RateCards/` goes in
 * the second delivery — Products must not import from a folder that dies.
 */
import React, { useMemo } from 'react'
import { Box, Button, Chip, MenuItem, Select, Stack, Typography } from '@mui/material'
import { dsColorNew } from '@palette'
import { LayersOutlined as LayersIcon } from 'Pages/DsRepricing/@components/icons'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { usePricelistTiers } from 'Pages/DsRepricing/Setup/@services/pricelists'
import {
	mergeTiers, tiersToSave, referenceLorOptions, setReferenceLor, tierKey, tierLabel,
} from 'Pages/DsRepricing/Setup/@services/tiers'
import { USE_MOCKS } from 'Pages/DsRepricing/Setup/@services/flag'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'

export default function TierEditor({ face, onChange }) {
	const canonical = usePricelistTiers(face.read_pricelist)
	const rows = useMemo(() => mergeTiers(canonical, face.tier_config || []),
	                     [canonical, face.tier_config])
	const stale = rows.some(r => r.status !== 'ok')

	if (!face.read_pricelist) return null
	if (!canonical.length) {
		return (
			<Typography sx={{ mt: 0.75, fontSize: 11, color: dsColorNew.textSubtle }}>
				{USE_MOCKS
					? 'Tier structure comes from the PMS sync — not available in mock mode.'
					: 'No tier structure synced for this pricelist yet.'}
			</Typography>
		)
	}

	return (
		<Box sx={{ mt: 0.75, pl: 0.25 }}>
			<Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.25 }}>
				<LayersIcon sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/>
				<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, fontWeight: 600 }}>
					Reference LOR per tier
				</Typography>
				{stale && (
					<Chip size="small" label="review" sx={toneChipSx('warn')}/>
				)}
			</Stack>
			<Stack gap={0.25}>
				{rows.map(r => {
					const key = tierKey(r)
					const gone = r.status === 'gone'
					return (
						<Stack key={key} direction="row" alignItems="center" gap={0.5}>
							<Typography sx={{
								minWidth: 54, fontSize: 11,
								fontFamily: 'inherit',
								color: gone ? dsColorNew.textSubtle : dsColorNew.textMuted,
								textDecoration: gone ? 'line-through' : 'none',
							}}>
								{tierLabel(r)} d
							</Typography>
							{gone ? (
								<Typography sx={{ fontSize: 11, color: dsColorNew.textSubtle }}>
									dropped by the PMS — removed on save
								</Typography>
							) : (
								<>
									<Select size="small"
									        value={r.reference_lor}
									        onChange={(e) => onChange(tiersToSave(
									          setReferenceLor(rows, key, Number(e.target.value))))}
									        sx={{ ...inputSx, minWidth: 64,
									              '& .MuiSelect-select': { py: 0.25, fontSize: 11 },
									              ...(r.status === 'new' && { bgcolor: dsColorNew.successBg }) }}>
										{referenceLorOptions(r).map(lor => (
											<MenuItem key={lor} value={lor} sx={{ fontSize: 11.5 }}>
												{lor} d
											</MenuItem>
										))}
									</Select>
									{r.status === 'new' && (
										<Typography sx={{ fontSize: 11, color: dsColorNew.successText }}>new</Typography>
									)}
								</>
							)}
						</Stack>
					)
				})}
			</Stack>
			{stale && (
				<Button size="small" variant="text"
				        onClick={() => onChange(tiersToSave(rows))}
				        sx={{ mt: 0.25, fontSize: 11, py: 0 }}>
					Reconcile with the PMS structure
				</Button>
			)}
		</Box>
	)
}
